import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * A label, its control, and the line under it — a hint, or the error that
 * replaces it.
 *
 * The same three pieces were placed by hand under 186 `<Label>`s with as many
 * spacings (`space-y-2`, `grid gap-2`, a hint in `text-xs` or `text-sm`), so two
 * fields of one form rarely lined up. The control is the child and keeps its
 * own props; `htmlFor` must match its `id` for the label to focus it.
 *
 * `horizontal` is the switch row: label and hint on the left, the control on
 * the right, for a yes/no that does not deserve a line of its own.
 */
export function Field({
  label,
  htmlFor,
  hint,
  error,
  required,
  orientation = "vertical",
  className,
  children,
}: {
  label: React.ReactNode;
  htmlFor?: string;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  orientation?: "vertical" | "horizontal";
  className?: string;
  children: React.ReactNode;
}) {
  const heading = (
    <Label htmlFor={htmlFor}>
      {label}
      {required && (
        <span className="ml-0.5 text-danger" aria-hidden>
          *
        </span>
      )}
    </Label>
  );
  const note = error ? (
    <p role="alert" className="text-xs text-danger">
      {error}
    </p>
  ) : hint ? (
    <p className="text-xs text-muted-foreground">{hint}</p>
  ) : null;

  if (orientation === "horizontal") {
    return (
      <div
        data-slot="field"
        className={cn("flex items-center justify-between gap-4", className)}
      >
        <div className="min-w-0 space-y-1">
          {heading}
          {note}
        </div>
        <div className="shrink-0">{children}</div>
      </div>
    );
  }

  return (
    <div data-slot="field" className={cn("space-y-1.5", className)}>
      {heading}
      {children}
      {note}
    </div>
  );
}
